/**
 * HomePage.js
 * ============
 * The primary gaze-navigation screen. Renders either a 3-block or 2-block
 * layout depending on the selectionMode set in GazeConfigContext, and handles
 * selection confirmation in either long-gaze or double-blink mode.
 *
 * Layout modes (selectionMode from config)
 * ─────────────────────────────────────────
 * '3-blocks'  — 4-quadrant card: top / left / right / bottom (current default)
 * '2-blocks'  — binary card: left / right only (no top or bottom interactive blocks)
 *
 * Confirmation modes (confirmationMode from config)
 * ──────────────────────────────────────────────────
 * 'long-gaze'    — gaze threshold fires navigation immediately; no pending state.
 *                  Looking away during buildup resets the window (existing behaviour).
 * 'double-blink' — gaze threshold sets a pendingNode (highlighted green); a double
 *                  blink confirms navigation; looking 'up' cancels; 45 s auto-cancel.
 */

import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getRootNodes } from './responseData';
import { getBinaryRootNodes } from './responseBinaryData';
import { useGazeNavigation, DIRECTION_TO_QUADRANT_INDEX } from './useGazeNavigation';
import { useGazeConfig, COLOR_MAP } from './GazeConfigContext';
import EditableQuadLabel from './EditableQuadLabel';
import { speakSegments } from './speakUtils';
import useReadAloud from './useReadAloud';
import './QuadrantPage.css';

// Auto-cancel the pending selection after 45 s if neither blink nor look-up occurs.
const CONFIRM_TIMEOUT_MS = 45_000;

// How long to show the green highlight in long-gaze mode before navigating (ms).
const LONG_GAZE_FEEDBACK_MS = 400;

export default function HomePage() {
  const navigate   = useNavigate();
  const { config, windowSize, calibration, labelOverrides, setLabelOverride } = useGazeConfig();
  const isBinary   = config.selectionMode === '2-blocks';
  const isLongGaze = config.confirmationMode === 'long-gaze';

  const colorVars = {
    '--q-bg':   COLOR_MAP[config.backgroundColor] ?? '#ffffff',
    '--q-text': COLOR_MAP[config.textColor]        ?? '#000000',
  };

  // Load the correct response tree for the active layout mode.
  // 3-blocks: top / left / right / bottom → items[0..3]
  // 2-blocks: left / right               → items[0..1]
  const items = isBinary ? getBinaryRootNodes() : getRootNodes();

  // ── Auto-announce options via TTS when readAloud is on ───────────────────
  const readAloudSegments = isBinary ? [
    items[0] ? ['Look left for',  items[0].label] : null,
    items[1] ? ['Look right for', items[1].label] : null,
  ].filter(Boolean) : [
    items[0] ? ['Look up for',    items[0].label] : null,
    items[1] ? ['Look left for',  items[1].label] : null,
    items[2] ? ['Look right for', items[2].label] : null,
    items[3] ? ['Look down for',  items[3].label] : null,
  ].filter(Boolean);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useReadAloud(readAloudSegments, [isBinary, items.map(i => i?.id).join(',')]);

  // ── Speak the selected label, interrupting any auto-announce ─────────────
  function speakLabel(label) {
    if (!config.readAloud || !window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(label);
    u.rate = 0.82; u.pitch = 1.0; u.volume = 1.0;
    if (config.voiceURI) {
      const voice = window.speechSynthesis.getVoices().find(v => v.voiceURI === config.voiceURI);
      if (voice) u.voice = voice;
    }
    window.speechSynthesis.speak(u);
  }

  // ── Double-blink confirmation state (only used in 'double-blink' mode) ────
  // pendingNode: the node awaiting confirmation; null = idle.
  const [pendingNode, setPendingNode]   = useState(null);
  const pendingNodeRef  = useRef(null);   // mirrors pendingNode for RAF / effect callbacks
  const pendingTimerRef = useRef(null);   // handle for the 45 s auto-cancel timer

  // Keep ref in sync with state so doubleBlinkCount effect always sees current value.
  useEffect(() => { pendingNodeRef.current = pendingNode; }, [pendingNode]);

  function clearPending() {
    setPendingNode(null);
    pendingNodeRef.current = null;
    if (pendingTimerRef.current) {
      clearTimeout(pendingTimerRef.current);
      pendingTimerRef.current = null;
    }
  }

  // Cleanup timer on unmount.
  useEffect(() => () => clearTimeout(pendingTimerRef.current), []);

  // ── Handle a node selection ───────────────────────────────────────────────
  function handleClick(node) {
    if (pendingNodeRef.current !== null) return; // already waiting

    speakLabel(node.label);

    // Always set pendingNode so the block flashes green as visual feedback.
    setPendingNode(node);
    pendingNodeRef.current = node;

    if (isLongGaze) {
      // Long-gaze mode: show the green highlight briefly, then navigate.
      pendingTimerRef.current = setTimeout(() => {
        clearPending();
        navigate(`/response/${node.id}`);
      }, LONG_GAZE_FEEDBACK_MS);
    } else {
      // Double-blink mode: wait for user confirmation — auto-cancel after 45 s.
      pendingTimerRef.current = setTimeout(clearPending, CONFIRM_TIMEOUT_MS);
    }
  }

  // ── Gaze navigation hook ──────────────────────────────────────────────────
  const { videoRef, activeDirection, doubleBlinkCount } = useGazeNavigation({
    enabled: true,
    windowSize,
    calibration,
    onGazeSelect: (direction) => {
      if (!isLongGaze && pendingNodeRef.current !== null) {
        // Double-blink mode pending: only 'up' is recognised (cancels selection).
        if (direction === 'up') clearPending();
        return;
      }

      // Map gaze direction to a node in the current layout.
      if (isBinary) {
        // Binary: left → items[0], right → items[1]
        const binMap = { left: 0, right: 1 };
        const idx = binMap[direction];
        if (idx !== undefined && items[idx]) handleClick(items[idx]);
      } else {
        // 3-block quad layout: up[0] / left[1] / right[2] / down[3]
        const idx = DIRECTION_TO_QUADRANT_INDEX[direction];
        if (idx !== undefined && items[idx]) handleClick(items[idx]);
      }
    },
  });

  // ── Double-blink confirmation (double-blink mode only) ────────────────────
  useEffect(() => {
    if (doubleBlinkCount === 0) return;
    const node = pendingNodeRef.current;
    if (node === null) return;
    clearPending();
    navigate(`/response/${node.id}`);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doubleBlinkCount]);

  // ── On-demand read aloud ──────────────────────────────────────────────────
  function speakPage() {
    speakSegments(isBinary ? [
      items[0] ? ['Look left for',  items[0].label] : null,
      items[1] ? ['Look right for', items[1].label] : null,
    ].filter(Boolean) : [
      items[0] ? ['Look up for',    items[0].label] : null,
      items[1] ? ['Look left for',  items[1].label] : null,
      items[2] ? ['Look right for', items[2].label] : null,
      items[3] ? ['Look down for',  items[3].label] : null,
    ].filter(Boolean), config.voiceURI);
  }

  // ── CSS helpers ───────────────────────────────────────────────────────────

  // Dimming/highlighting during double-blink pending state.
  const blockClass = (node) => {
    if (pendingNode === null) return '';
    return node.id === pendingNode.id ? 'quad-selected' : 'quad-dimmed';
  };

  // Gaze highlight: suppressed while a selection is pending (double-blink mode).
  const gazeClass = (direction) => {
    if (!isLongGaze && pendingNode !== null) return '';
    return activeDirection === direction ? 'quad-active' : '';
  };

  // ── Render ────────────────────────────────────────────────────────────────

  if (isBinary) {
    // ── 2-block layout ──────────────────────────────────────────────────────
    const [left, right] = items;
    return (
      <div className="quad-page" style={colorVars}>
        <video ref={videoRef} playsInline muted
          style={{ position: 'absolute', width: 0, height: 0, opacity: 0 }} />
        <div className="quad-title-row">
          <button className="quad-title-home-btn" onClick={() => navigate('/landing')} aria-label="Go to home">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none"
              stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 9.5L12 3l9 6.5V20a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9.5z" />
              <polyline points="9 21 9 12 15 12 15 21" />
            </svg>
          </button>
          <span className="quad-title">Topics</span>
          <button className="quad-title-speak-btn" onClick={speakPage} aria-label="Read options aloud">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none"
              stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
              <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
              <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
            </svg>
          </button>
        </div>
        <div className="quad-card quad-card--binary-home">
          <div className="quad-middle">
            <div
              className={`quad-left ${left ? blockClass(left) : ''} ${gazeClass('left')}`}
              onClick={() => left && handleClick(left)}
            >
              {left && <EditableQuadLabel nodeId={left.id} defaultLabel={left.label} labelOverrides={labelOverrides} setLabelOverride={setLabelOverride} />}
            </div>
            <div
              className={`quad-right ${right ? blockClass(right) : ''} ${gazeClass('right')}`}
              onClick={() => right && handleClick(right)}
            >
              {right && <EditableQuadLabel nodeId={right.id} defaultLabel={right.label} labelOverrides={labelOverrides} setLabelOverride={setLabelOverride} />}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── 3-block (quad) layout ────────────────────────────────────────────────
  return (
    <div className="quad-page" style={colorVars}>
      <video ref={videoRef} playsInline muted
        style={{ position: 'absolute', width: 0, height: 0, opacity: 0 }} />
      <div className="quad-title-row">
          <button className="quad-title-home-btn" onClick={() => navigate('/landing')} aria-label="Go to home">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none"
              stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 9.5L12 3l9 6.5V20a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9.5z" />
              <polyline points="9 21 9 12 15 12 15 21" />
            </svg>
          </button>
          <span className="quad-title">Topics</span>
          <button className="quad-title-speak-btn" onClick={speakPage} aria-label="Read options aloud">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none"
              stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
              <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
              <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
            </svg>
          </button>
        </div>
      <div className="quad-card">
        <div
          className={`quad-top ${blockClass(items[0])} ${gazeClass('up')}`}
          onClick={() => handleClick(items[0])}
        >
          <EditableQuadLabel nodeId={items[0].id} defaultLabel={items[0].label} labelOverrides={labelOverrides} setLabelOverride={setLabelOverride} />
        </div>
        <div className="quad-middle">
          <div
            className={`quad-left ${blockClass(items[1])} ${gazeClass('left')}`}
            onClick={() => handleClick(items[1])}
          >
            <EditableQuadLabel nodeId={items[1].id} defaultLabel={items[1].label} labelOverrides={labelOverrides} setLabelOverride={setLabelOverride} />
          </div>
          <div
            className={`quad-right ${blockClass(items[2])} ${gazeClass('right')}`}
            onClick={() => handleClick(items[2])}
          >
            <EditableQuadLabel nodeId={items[2].id} defaultLabel={items[2].label} labelOverrides={labelOverrides} setLabelOverride={setLabelOverride} />
          </div>
        </div>
        <div
          className={`quad-bottom ${blockClass(items[3])} ${gazeClass('down')}`}
          onClick={() => handleClick(items[3])}
        >
          <EditableQuadLabel nodeId={items[3].id} defaultLabel={items[3].label} labelOverrides={labelOverrides} setLabelOverride={setLabelOverride} />
        </div>
      </div>
    </div>
  );
}
